/**
 * @module state/persist
 * Handles persistence of state to and from localStorage.
 *
 * On every action that passes through the store, the state
 *   is dehydrated and written to localStorage.
 * On every page load, the saved state is loaded from
 *   localStorage and used to rehydrate the current state.
 *
 * Dehydration is handled by {@link module:state/rootDehydrate}.
 * Hydration is handled by reducing the `STATE_HYDRATE` action.
 */

import {fromJS} from "immutable";
import logger from "lib/log";
export const STATE_HYDRATE = "@@persist/HYDRATE_STATE";
export const LOCALSTORAGE_KEY_NAME = "state";

/**
 * hydrateState - returns action for the hydrate state action
 */
function hydrateState(state) {
  return {
    type: STATE_HYDRATE,
    error: false,
    payload: {
      state
    }
  }
}

/**
 * saveState - serialize a state to a string for localStorage save
 */
function saveState(state) {
  try {
    const serialisedState = JSON.stringify(state);
    localStorage.setItem(LOCALSTORAGE_KEY_NAME, serialisedState)
  // localstorage not available, write error
  } catch (error) {
    logger.error("Unable to save state to local storage:", error)
  }
}

/**
 * loadState - attempt to load and parse the serialized state
 *
 * @returns {Object}
 */
function loadState() {
  // Load state from localStorage
  logger.debug("Checking for persisted state...");
  let rawState;
  try {
    rawState = localStorage.getItem(LOCALSTORAGE_KEY_NAME);
    // Key not set - return undefined as if not available
    if(rawState === null) {
      logger.debug("No persisted state could be found. ignoring...")
      return undefined;
    }
  } catch (error) {
    logger.error("Error retrieving state from localStorage:", error)
    return undefined;
  }

  logger.debug(`Found persisted state. parsing... (length:${rawState.length})`)
  try {
    return JSON.parse(rawState);
  } catch (error) {
    logger.error("Unable to load state from localStorage: ", error)
    return undefined;
  }
}


/**
 * attachPersistence - attach the persistence features to a given store object
 */
export function attachPersistence(store, transformState = ( (state) => state) ) {
  // Grab the persisted state from localstorage
  // if we have data, fire the hydrate action, leaving the reducers to merge state
  const persistedState = fromJS( loadState() );
  if(persistedState !== undefined) {
    store.dispatch( hydrateState(persistedState) );
  }

  // Listen for changes on the store and serialize each time
  store.subscribe( () => {
    saveState( transformState( store.getState() ) );
  })
}



// WEBPACK FOOTER //
// ./src/state/persist.js